import React, { useState, useMemo, useEffect, useRef, useId } from 'react';
import { GraduationCap, RotateCcw, Search } from 'lucide-react';
import CardActionsMenu from './CardActionsMenu';
import PracticeSettingsService from '../services/PracticeSettingsService';
import MarkdownText from './MarkdownText';
import ReadingContentItem from './ReadingContentItem';
import { stripSimpleMarkdown } from '../utils/simpleMarkdown';
import { getGraduationStamp } from '../utils/graduationStamp';
import { t } from '../i18n';
import './FlashCardMap.css';

function getDialogTurns(card) {
  if (Array.isArray(card?.dialogTurns) && card.dialogTurns.length > 0) {
    return card.dialogTurns;
  }

  const lines = String(card?.dialogContent || card?.back || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const turns = [];
  const taggedLines = [];
  let inStory = false;

  lines.forEach((line) => {
    if (/^([AB])\s+name:\s*/i.test(line)) return null;
    const storyMatch = line.match(/^story:\s*(.*)$/i);
    if (storyMatch) {
      inStory = true;
      const text = storyMatch[1].trim();
      if (text) {
        taggedLines.push({ type: 'story', text, nativeText: text, answerText: text, practice: true });
      }
      return null;
    }
    const sceneMatch = line.match(/^scene:\s*(.*)$/i);
    if (sceneMatch || line.startsWith('-')) {
      inStory = false;
      const text = sceneMatch
        ? sceneMatch[1].trim()
        : line.replace(/^-+\s*/, '').trim();
      if (text) {
        taggedLines.push({ type: 'situation', text, nativeText: text, answerText: null, practice: false });
      }
      return null;
    }
    const match = line.match(/^([AB]):\s*(.*)$/i);
    if (match && match[2].trim()) {
      inStory = false;
      taggedLines.push({ speaker: match[1].toUpperCase(), text: match[2].trim() });
      return null;
    }
    if (inStory) {
      taggedLines.push({ type: 'story', text: line, nativeText: line, answerText: line, practice: true });
    }
    return null;
  });
  if (taggedLines.some(Boolean)) {
    taggedLines.forEach((line) => {
      if (!line) return;
      if (line.type === 'situation' || line.type === 'story') {
        turns.push(line);
        return;
      }
      turns.push({
        speaker: line.speaker,
        nativeText: line.text,
        answerText: null,
        practice: false,
      });
    });
    return turns;
  }

  for (let index = 0; index < lines.length; index += 2) {
    const nativeText = lines[index] || '';
    const answerText = lines[index + 1] || '';
    if (nativeText && answerText) {
      turns.push({
        speaker: turns.length % 2 === 0 ? 'A' : 'B',
        nativeText,
        answerText,
        practice: true,
      });
    }
  }
  return turns;
}

function getReadingPreviewSegments(card) {
  if (Array.isArray(card?.readingSegments) && card.readingSegments.length > 0) {
    return card.readingSegments.map((segment) => {
      if (segment.type === 'scene') {
        return { type: 'situation', text: segment.text, nativeText: segment.text, practice: false };
      }
      if (segment.type === 'story') {
        return { type: 'story', text: segment.text, nativeText: segment.text, practice: true };
      }
      return {
        speaker: segment.speaker === 'B' ? 'B' : 'A',
        nativeText: segment.text || '',
        answerText: null,
        practice: true,
      };
    });
  }
  return getDialogTurns(card);
}

function getCardSearchText(card) {
  const readingSegmentText = Array.isArray(card?.readingSegments)
    ? card.readingSegments.map((segment) => segment?.text || segment?.nativeText || '')
    : [];
  const dialogText = Array.isArray(card?.dialogTurns)
    ? card.dialogTurns.flatMap((turn) => [turn?.text, turn?.nativeText, turn?.answerText])
    : [];

  return [
    card?.front,
    card?.back,
    // Old records may still contain these fields. They remain searchable during
    // the transition, but new level-ups create independent cards instead.
    card?.levelUpQuestion,
    card?.levelUpAnswer,
    card?.readingTitle,
    card?.readingContent,
    ...(Array.isArray(card?.readingSentences) ? card.readingSentences : []),
    ...readingSegmentText,
    ...dialogText,
  ]
    .map((value) => stripSimpleMarkdown(String(value || '')))
    .join('\n')
    .toLocaleLowerCase();
}

function getMapProgress(card) {
  const children = Array.isArray(card?.readingChildren)
    ? card.readingChildren.filter((child) => !child?.retiredAt)
    : [];

  if (card?.cardType !== 'reading' || children.length === 0) {
    return {
      practiceCount: Number(card?.practiceCount || 0),
      score: Number(card?.score || 0),
    };
  }

  return {
    practiceCount: Math.round(
      children.reduce((total, child) => total + Number(child?.practiceCount || 0), 0) / children.length
    ),
    score: children.reduce((total, child) => total + Number(child?.score || 0), 0),
  };
}

/**
 * FlashCardMap component displays all flash cards in a grid layout
 * with Q/A and Reading search in newest-first order
 * 
 * @param {Object} props
 * @param {Array} props.flashCards - Array of flash card objects
 * @param {Function} props.onCardSelect - Callback when a card is selected
 * @param {Function} props.onCardEdit - Callback when edit button is clicked
 */
function FlashCardMap({ flashCards = [], onCardSelect, onCardEdit, onCardReview, readOnly = false, showControls = true, showProgress = true, preserveOrder = false, showAnswersFirst = false }) {
  const [searchText, setSearchText] = useState('');
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [articlePreviewCard, setArticlePreviewCard] = useState(null);
  const searchInputRef = useRef(null);
  const previewDialogRef = useRef(null);
  const previewCloseRef = useRef(null);
  const previewTitleId = useId();
  const searchInputId = useId();
  const searchResultsId = useId();

  useEffect(() => {
    if (!articlePreviewCard) return undefined;
    const returnFocusTarget = document.activeElement;
    previewCloseRef.current?.focus();
    return () => {
      if (returnFocusTarget?.isConnected) returnFocusTarget.focus();
    };
  }, [articlePreviewCard]);

  const handlePreviewKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setArticlePreviewCard(null);
    } else if (event.key === 'Tab') {
      const focusable = Array.from(previewDialogRef.current?.querySelectorAll(
        'button:not(:disabled), [tabindex="0"]'
      ) || []);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  const handleCardClick = (card) => {
    const cardType = PracticeSettingsService.getCardType(card);
    if (cardType === PracticeSettingsService.CARD_TYPES.READING) {
      const hasCompletedSentence = card.isReadingGroup && card.readingChildren?.some((child) => !child.retiredAt && PracticeSettingsService.isGraduated({ ...child, cardType: 'reading' }));
      if (!readOnly && onCardReview && (PracticeSettingsService.isGraduated(card) || hasCompletedSentence)) { onCardReview(card); return; }
      setArticlePreviewCard(card);
      return;
    }

    // Toggle flip state only - don't call onCardSelect
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(card.id)) {
        newSet.delete(card.id);
      } else {
        newSet.add(card.id);
      }
      return newSet;
    });
  };


  // The map presents overall learning progress, not assessment history alone.
  const getMapGrade = (card) => {
    return PracticeSettingsService.getPracticeIntensityLevel({
      ...card,
      ...getMapProgress(card),
    });
  };

  const formatGradeValue = (grade) => {
    if (grade === null || grade === undefined || grade <= 0) {
      return '-';
    }

    return String(grade);
  };

  // Search never changes the map's default newest-first order or source array.
  const processedFlashCards = useMemo(() => {
    let filtered = [...flashCards];

    const normalizedSearch = searchText.trim().toLocaleLowerCase();
    if (normalizedSearch) {
      filtered = filtered.filter((card) => getCardSearchText(card).includes(normalizedSearch));
    }

    if (!preserveOrder) filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    return filtered;
  }, [flashCards, searchText, preserveOrder]);

  if (flashCards.length === 0) {
    return (
      <div className="flashcard-map-empty">
        <p>{t('learningNavigation.map.empty')}</p>
      </div>
    );
  }

  return (
    <div className="flashcard-map">
      {showControls && (
        <div className="flashcard-map-search-bar" role="search" aria-label={t('learningNavigation.map.searchRegion')}>
          <div className="flashcard-map-search">
            <div className="map-search-field">
              <Search size={17} strokeWidth={2.2} aria-hidden="true" />
              <input
                ref={searchInputRef}
                id={searchInputId}
                aria-label={t('learningNavigation.map.searchLabel')}
                aria-describedby={searchResultsId}
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t('learningNavigation.map.searchPlaceholder')}
              />
            </div>
          </div>
          <div className="flashcard-map-results">
            <span id={searchResultsId} role="status">{t('learningNavigation.map.results', undefined, { count: processedFlashCards.length, total: flashCards.length })}</span>
            {searchText && (
              <button type="button" onClick={() => { setSearchText(''); searchInputRef.current?.focus(); }}>
                {t('learningNavigation.map.clearSearch')}
              </button>
            )}
          </div>
        </div>
      )}

      {processedFlashCards.length === 0 ? (
        <div className="flashcard-map-empty">
          <p>{t('learningNavigation.map.noMatches')}</p>
        </div>
      ) : (
        <div className="flashcard-map-grid">
          {processedFlashCards.map((card) => {
            const lastGrade = getMapGrade(card);
            const { practiceCount, score } = getMapProgress(card);
            const isCompleted = PracticeSettingsService.isCardCompleted(card);
            const cardLevel = PracticeSettingsService.getCardLevel(card);
            
            // Determine score level for visual indicators
            let practiceLevel = 'none';
            if (score === 0) {
              practiceLevel = 'none';
            } else if (score < 100) {
              practiceLevel = 'low';
            } else if (score < 300) {
              practiceLevel = 'medium';
            } else if (score < 500) {
              practiceLevel = 'high';
            } else {
              practiceLevel = 'very-high';
            }
            
            // Determine font size based on visual width (accounting for CJK characters)
            const cardType = PracticeSettingsService.getCardType(card);
            const isReadingCard = cardType === PracticeSettingsService.CARD_TYPES.READING;
            // Q/A order changes the text, not the card's current physical face.
            const isFlipped = !isReadingCard && flippedCards.has(card.id);
            const showingAnswer = isFlipped !== showAnswersFirst;
            const graduationType = isReadingCard
              ? PracticeSettingsService.GRADUATION_TYPES.SCORE
              : PracticeSettingsService.getCardGraduationType(card) || PracticeSettingsService.GRADUATION_TYPES.MANUAL;
            const isArticleCard = isReadingCard;
            const displayText = isReadingCard
              ? (card.readingTitle || card.front)
              : (showingAnswer ? card.back : card.front);
            let fontSizeClass = '';
            if (displayText) {
              const plainText = stripSimpleMarkdown(displayText);
              // CJK characters (Chinese, Japanese, Korean) are typically wider
              const cjkRegex = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf\uac00-\ud7a3]/g;
              const cjkMatches = plainText.match(cjkRegex) || [];
              const cjkCount = cjkMatches.length;
              const nonCjkCount = plainText.length - cjkCount;
              
              // Weight CJK characters more heavily (approximately 1.5x wider)
              // Lowered threshold to 60 to catch medium-length content
              const visualWidth = nonCjkCount + (cjkCount * 1.5);
              fontSizeClass = visualWidth > 60 ? 'small-font' : '';
            }
            
            return (
              <div
                key={card.id}
                className={`flashcard-map-item ${showProgress ? `practice-${practiceLevel}` : 'is-public-note-card'} ${isFlipped ? 'flipped' : ''} ${showProgress && isCompleted ? 'completed' : ''} ${isArticleCard ? 'reading-card' : ''}`}
                onClick={isArticleCard ? () => handleCardClick(card) : undefined}
              >
                {showProgress && isCompleted && (
                  <img
                    className="flashcard-map-completion-stamp"
                    src={getGraduationStamp(graduationType)}
                    alt={isReadingCard ? 'Reading completion stamp' : `${graduationType} graduation stamp`}
                  />
                )}
                <button
                  type="button"
                  className="flashcard-map-content"
                  onClick={isArticleCard ? undefined : () => handleCardClick(card)}
                  aria-label={isArticleCard
                    ? t('learningNavigation.map.preview', undefined, { text: stripSimpleMarkdown(displayText || t('learningNavigation.map.untitledReading')) })
                    : t(showingAnswer ? 'learningNavigation.map.showQuestion' : 'learningNavigation.map.showAnswer', undefined, { text: stripSimpleMarkdown(displayText || t('learningNavigation.map.card')) })}
                  aria-haspopup={isArticleCard ? 'dialog' : undefined}
                >
                  <span className={`flashcard-map-text ${fontSizeClass}`}>
                    <MarkdownText text={displayText} />
                  </span>
                </button>
                {(isArticleCard || showProgress || !readOnly) && (
                  <div className="flashcard-map-stats">
                    {isArticleCard && (
                      <span className="flashcard-map-type-line">
                        {t('learningNavigation.common.reading')}
                      </span>
                    )}
                    <div className="flashcard-map-stat-line">
                      {showProgress && <>
                        <span className="practice-icon" aria-hidden="true">{'\u21BB'}</span>
                        <span className="flashcard-map-stat-value">{practiceCount}</span>
                        <span className="flashcard-map-stat-separator" aria-hidden="true">·</span>
                        <span className="practice-icon" aria-hidden="true">{'\u2605'}</span>
                        <span className="flashcard-map-stat-value">{score}</span>
                        <span className="flashcard-map-stat-separator" aria-hidden="true">·</span>
                        <GraduationCap className="grade-label" size={14} role="img" aria-label={t('learningNavigation.map.gradeShort')} />
                        <span className="flashcard-map-stat-value">{formatGradeValue(lastGrade)}</span>
                        {cardLevel > 1 && <>
                          <span className="flashcard-map-stat-separator" aria-hidden="true">·</span>
                          <span className="flashcard-map-level-badge" title={t('learningNavigation.common.level', undefined, { level: cardLevel })}>{`Lv.${cardLevel}`}</span>
                        </>}
                      </>}
                      {!readOnly && (
                        isCompleted ? (onCardReview && <button type="button" className="flashcard-map-edit-btn"
                          aria-label={t('learningNavigation.map.reviewLevelUp')} title={t('learningNavigation.map.reviewLevelUp')}
                          onClick={event => { event.stopPropagation(); onCardReview(card); }}>
                          <RotateCcw size={20} aria-hidden="true" />
                        </button>) : <CardActionsMenu onEdit={onCardEdit ? () => onCardEdit(card) : undefined}
                          onReview={onCardReview ? () => onCardReview(card) : undefined} />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showControls && showProgress && <>
      <div className="flashcard-map-legend">
        <div className="legend-title">{t('learningNavigation.map.legend')}</div>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-indicator practice-none"></span>
            <span className="legend-label">0</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator practice-low"></span>
            <span className="legend-label">1-99</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator practice-medium"></span>
            <span className="legend-label">100-299</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator practice-high"></span>
            <span className="legend-label">300-499</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator practice-very-high"></span>
            <span className="legend-label">500+</span>
          </div>
        </div>
      </div>
      </>}

      {articlePreviewCard && (
        <div
          className="modal-overlay flashcard-map-reading-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setArticlePreviewCard(null); }}
        >
          <div
            ref={previewDialogRef}
            className="flashcard-map-reading-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={previewTitleId}
            onKeyDown={handlePreviewKeyDown}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id={previewTitleId}>
                {articlePreviewCard.readingTitle || articlePreviewCard.front}
              </h2>
              <button
                ref={previewCloseRef}
                type="button"
                className="modal-close"
                onClick={() => setArticlePreviewCard(null)}
                aria-label={t('learningNavigation.map.closePreview')}
              >
                {'\u00D7'}
              </button>
            </div>
            <div className="flashcard-map-reading-body" tabIndex={0} role="document" aria-label={t('learningNavigation.map.articleText')}>
              {Array.isArray(articlePreviewCard.readingSegments) && articlePreviewCard.readingSegments.length > 0 ? (
                <div className="flashcard-map-dialog-preview">
                  {getReadingPreviewSegments(articlePreviewCard).map((turn, index) => (
                    <ReadingContentItem
                      key={index}
                      item={{
                        type: turn.type === 'situation' ? 'scene' : turn.type === 'story' ? 'story' : 'speech',
                        text: turn.text || turn.nativeText || '',
                        speaker: turn.speaker,
                        segmentIndex: index,
                      }}
                      characterNames={articlePreviewCard.readingCharacterNames || articlePreviewCard.dialogCharacterNames}
                      speechText={turn.nativeText || ''}
                    />
                  ))}
                </div>
              ) : (
                <MarkdownText text={articlePreviewCard.readingContent || articlePreviewCard.back || ''} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlashCardMap;
